<i18n locale="en">
{
  "actions": {
    "down": "Move down",
    "remove": "Remove",
    "up": "Move up"
  },
  "labels": {
    "addImage": "Add photo",
    "addVideo": "Add video",
    "customThumbnail": "Custom thumbnail",
    "image": "Photo",
    "video": "Video"
  }
}
</i18n>

<template>
  <div class="gallery-media-input">
    <div class="gallery-media-input__add-grid">
      <div>
        <div class="gallery-media-input__label">{{ t('labels.addImage') }}</div>
        <ImageInput
          :aspect-ratio="1"
          :crop-width="1600"
          :label="t('labels.addImage')"
          hide-edit-button
          @update:model-value="addImage"
        />
      </div>
      <div>
        <div class="gallery-media-input__label">{{ t('labels.addVideo') }}</div>
        <HostedVideoInput
          :brand-id="brandId"
          :model-value="null"
          @update:model-value="addVideo"
        />
      </div>
    </div>

    <div v-if="items.length" class="gallery-media-input__list">
      <div
        v-for="(item, index) in items"
        :key="itemKey(item, index)"
        class="gallery-media-input__item"
      >
        <div class="gallery-media-input__thumb">
          <img :src="thumbnail(item)" alt="" />
          <span
            v-if="item.type === 'video'"
            aria-hidden="true"
            class="gallery-media-input__play"
          />
        </div>
        <div class="gallery-media-input__details">
          <strong>{{ t(`labels.${item.type}`) }}</strong>
          <ImageInput
            v-if="item.type === 'video'"
            :aspect-ratio="1"
            :crop-width="1600"
            :label="t('labels.customThumbnail')"
            :model-value="item.params.thumbnail"
            :upload-label="t('labels.customThumbnail')"
            @update:model-value="updateThumbnail(index, $event)"
          />
        </div>
        <div class="gallery-media-input__actions">
          <OwnButton
            :text="t('actions.up')"
            :disabled="index === 0"
            small
            variant="tertiary"
            @click="move(index, -1)"
          />
          <OwnButton
            :text="t('actions.down')"
            :disabled="index === items.length - 1"
            small
            variant="tertiary"
            @click="move(index, 1)"
          />
          <OwnButton
            :text="t('actions.remove')"
            small
            variant="danger"
            @click="remove(index)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n'

import { ImageInput } from '@/components/inputs/ImageInput'
import HostedVideoInput, {
  type HostedVideoValue,
} from '@/features/website-builder/builder/components/HostedVideoInput.vue'
import { OwnButton } from '@/ui/OwnButton'

type GalleryMediaItem =
  | { type: 'image'; params: { src: string; alt: string | null } }
  | {
      type: 'video'
      params: HostedVideoValue & { alt: string | null }
    }

const props = defineProps<{
  brandId: string
  items: GalleryMediaItem[]
}>()

const emit = defineEmits<{
  change: [
    payload: {
      data: GalleryMediaItem[]
      type: 'upload' | 'remove' | 'reorder'
    },
  ]
}>()

const { t } = useI18n()

const itemKey = (item: GalleryMediaItem, index: number): string =>
  item.type === 'image'
    ? `${item.params.src}-${index}`
    : `${item.params.mp4}-${index}`

const thumbnail = (item: GalleryMediaItem): string =>
  item.type === 'image' ? item.params.src : item.params.thumbnail

const addImage = (src: string | null): void => {
  if (!src) return
  emit('change', {
    data: [...props.items, { params: { alt: null, src }, type: 'image' }],
    type: 'upload',
  })
}

const addVideo = (video: HostedVideoValue | null): void => {
  if (!video) return
  emit('change', {
    data: [...props.items, { params: { alt: null, ...video }, type: 'video' }],
    type: 'upload',
  })
}

const updateThumbnail = (index: number, thumbnailUrl: string | null): void => {
  const item = props.items[index]
  if (!thumbnailUrl || item?.type !== 'video') return
  const data = [...props.items]
  data[index] = {
    ...item,
    params: { ...item.params, thumbnail: thumbnailUrl },
  }
  emit('change', { data, type: 'upload' })
}

const move = (index: number, offset: number): void => {
  const target = index + offset
  if (target < 0 || target >= props.items.length) return
  const data = [...props.items]
  const currentItem = data[index]
  const targetItem = data[target]
  if (!currentItem || !targetItem) return
  data[index] = targetItem
  data[target] = currentItem
  emit('change', { data, type: 'reorder' })
}

const remove = (index: number): void => {
  emit('change', {
    data: props.items.filter((_, itemIndex) => itemIndex !== index),
    type: 'remove',
  })
}
</script>

<style lang="scss" scoped>
.gallery-media-input {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__add-grid {
    display: grid;
    gap: 16px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  &__label {
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 8px;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__item {
    align-items: flex-start;
    border: 1px solid var(--divider);
    border-radius: 12px;
    display: grid;
    gap: 12px;
    grid-template-columns: 88px minmax(0, 1fr);
    padding: 12px;
  }

  &__thumb {
    aspect-ratio: 1;
    border-radius: 8px;
    overflow: hidden;
    position: relative;
  }

  &__thumb img {
    height: 100%;
    object-fit: cover;
    width: 100%;
  }

  &__play {
    align-items: center;
    background: rgba(255, 255, 255, 0.92);
    border-radius: 999px;
    display: flex;
    height: 34px;
    justify-content: center;
    left: 50%;
    padding-left: 2px;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 34px;
  }

  &__play::before {
    border-bottom: 6px solid transparent;
    border-left: 9px solid #111;
    border-top: 6px solid transparent;
    content: '';
  }

  &__details {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__actions {
    display: flex;
    gap: 6px;
    grid-column: 1 / -1;
  }
}
</style>
